<template>
  <div class="p-6 min-h-screen bg-gray-50 dark:bg-gray-950">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-3">
        <span class="material-icons text-3xl text-blue-600">security</span>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Centro Antifrode</h1>
      </div>
      <div class="flex items-center gap-3 text-sm text-gray-500">
        <span v-if="lastRefresh">Ultimo agg.: {{ lastRefreshLabel }}</span>
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-300 hover:bg-white text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition"
          :class="{ 'opacity-60 cursor-wait': loading }"
          :disabled="loading"
          @click="refresh"
        >
          <span class="material-icons text-sm" :class="{ 'animate-spin': loading }">refresh</span>
          Aggiorna
        </button>
      </div>
    </div>

    <!-- Skeleton caricamento -->
    <div v-if="loading && !summary" class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
      <div
        v-for="i in 3"
        :key="i"
        class="h-28 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- KPI cards -->
    <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
      <!-- Rischio Alto -->
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition border-l-4 border-red-500"
        @click="$router.push({ name: 'menu.antifrode.utenti', query: { min_score: 61 } })"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">A rischio non bloccati</span>
          <span class="material-icons text-red-500">warning</span>
        </div>
        <p class="text-4xl font-bold text-gray-900 dark:text-white">
          {{ summary?.users_high_risk ?? '—' }}
        </p>
        <p class="text-xs text-gray-400 mt-1">Utenti con score ≥ 61 non in blacklist</p>
      </div>

      <!-- Account Bloccati -->
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition border-l-4 border-gray-600"
        @click="$router.push({ name: 'menu.antifrode.bloccati' })"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Account Bloccati</span>
          <span class="material-icons text-gray-600">block</span>
        </div>
        <p class="text-4xl font-bold text-gray-900 dark:text-white">
          {{ summary?.users_blocked ?? '—' }}
        </p>
        <p class="text-xs text-gray-400 mt-1">In blacklist</p>
      </div>

      <!-- Tentativi sospetti 72h -->
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition border-l-4 border-yellow-500"
        @click="$router.push({ name: 'menu.antifrode.tentativi' })"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Tentativi 72h</span>
          <span class="material-icons text-yellow-500">person_add_disabled</span>
        </div>
        <p class="text-4xl font-bold text-gray-900 dark:text-white">
          {{ summary?.signup_attempts_72h ?? '—' }}
        </p>
        <p class="text-xs text-gray-400 mt-1">Signup sospetti</p>
      </div>
    </div>

    <!-- Alert recenti -->
    <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
      <div class="flex items-center gap-2 mb-4">
        <span class="material-icons text-yellow-500">notifications_active</span>
        <h2 class="font-semibold text-gray-900 dark:text-white">Alert recenti</h2>
        <span class="ml-auto text-xs text-gray-400">Score ≥ 70</span>
      </div>

      <div v-if="loading && !alerts.length" class="space-y-3">
        <div
          v-for="i in 3"
          :key="i"
          class="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"
        ></div>
      </div>

      <div v-else-if="!alerts.length" class="text-center py-8 text-gray-400">
        <span class="material-icons text-4xl mb-2 block">check_circle</span>
        Nessun alert critico. Buon lavoro!
      </div>

      <ul v-else class="divide-y divide-gray-100 dark:divide-gray-800">
        <li
          v-for="alert in alerts"
          :key="alert.uid"
          class="py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 -mx-5 px-5 cursor-pointer transition rounded"
          @click="$router.push({ name: 'menu.antifrode.utenti.detail', params: { uid: alert.uid } })"
        >
          <!-- Score badge -->
          <span
            :class="scoreBadgeClass(alert.score)"
            class="mt-0.5 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
          >
            {{ alert.score }}
          </span>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <p class="font-medium text-gray-900 dark:text-white truncate">
              {{ alert.name || 'Utente #' + alert.uid }}
              <span class="text-xs text-gray-400 ml-1">#{{ alert.uid }}</span>
            </p>
            <p class="text-sm text-gray-500 truncate">{{ alert.email }}</p>
            <!-- Segnali sintetici -->
            <div v-if="alert.signals && alert.signals.length" class="flex flex-wrap gap-1 mt-1">
              <span
                v-for="sig in alert.signals.filter(s => s.points > 0).slice(0, 3)"
                :key="sig.code"
                class="text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded"
              >
                {{ sig.label }}
              </span>
            </div>
          </div>

          <!-- Arrow -->
          <span class="material-icons text-gray-300 mt-2 flex-shrink-0">chevron_right</span>
        </li>
      </ul>

      <!-- Link "vedi tutti" -->
      <div v-if="alerts.length" class="mt-4 text-center">
        <button
          class="text-sm text-blue-600 hover:underline"
          @click="$router.push({ name: 'menu.antifrode.utenti', query: { min_score: 61 } })"
        >
          Vedi tutti gli utenti a rischio alto →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      lastRefresh: null,
      refreshTimer: null,
    };
  },
  computed: {
    ...mapGetters(['antifrode_summary', 'antifrode_loading']),
    summary() {
      return this.antifrode_summary;
    },
    loading() {
      return this.antifrode_loading;
    },
    alerts() {
      return (this.summary?.alert_recenti || []).filter((a) => a.score >= 70);
    },
    lastRefreshLabel() {
      if (!this.lastRefresh) return '';
      const diffMs = Date.now() - this.lastRefresh;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'adesso';
      if (diffMin === 1) return '1 min fa';
      return `${diffMin} min fa`;
    },
  },
  mounted() {
    this.refresh();
    // Auto-refresh ogni 60 secondi
    this.refreshTimer = setInterval(() => {
      this.refresh();
    }, 60000);
  },
  beforeUnmount() {
    clearInterval(this.refreshTimer);
  },
  methods: {
    async refresh() {
      await this.$store.dispatch('fetchAntifrodeSummary');
      this.lastRefresh = Date.now();
    },
    formatEur(val) {
      if (val == null) return '—';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(val);
    },
    scoreBadgeClass(score) {
      if (score >= 70) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      if (score >= 40) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    },
  },
};
</script>

<style></style>
